import { ref, computed } from 'vue';
import type { CustomerAddress } from '@/types/customer';

export const useCustomerAddresses = () => {
  const customerAddresses = ref<CustomerAddress[]>([]);
  const selectedAddressId = ref<number | undefined>(undefined);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const hasMultipleAddresses = computed(() => {
    return customerAddresses.value.length > 1;
  });

  const shouldShowAddresses = (orderType: 'Delivery' | 'Takeaway') => {
    // Only show addresses for Delivery orders
    return orderType === 'Delivery';
  };

  const loadCustomerAddresses = async (customerId: number, initialSelectedAddressId?: number | null) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      // Use the API service instead of direct fetch
      const api = await import('@/services/api').then(m => m.default);
      const response = await api.get(`/customers/addresses/customer/${customerId}`);
      
      // Handle the response properly - axios interceptor flattens the response
      const addresses = response.data || response;
      
      if (Array.isArray(addresses)) {
        customerAddresses.value = addresses;
        handleAddressSelection(addresses, initialSelectedAddressId);
      } else if (addresses && typeof addresses === 'object') {
        // Handle case where response is an object with addresses array
        const addressArray = addresses.addresses || addresses;
        customerAddresses.value = Array.isArray(addressArray) ? addressArray : [];
        handleAddressSelection(customerAddresses.value, initialSelectedAddressId);
      } else {
        console.warn('Unexpected addresses format:', addresses);
        customerAddresses.value = [];
      }
    } catch (err) {
      console.error('Error loading customer addresses:', err);
      error.value = 'Failed to load addresses';
      
      if (err && typeof err === 'object' && 'response' in err) {
        const errorResponse = err as any;
        console.error('Error details:', errorResponse.response?.data, errorResponse.response?.status);
      }
    } finally {
      isLoading.value = false;
    }
  };

  const handleAddressSelection = (addresses: CustomerAddress[], initialSelectedAddressId?: number | null) => {
    // Auto-select default address
    let defaultAddress = addresses.find(addr => addr.is_default);
    
    // If no default address and only one address, select it as default
    if (!defaultAddress && addresses.length === 1) {
      defaultAddress = addresses[0];
    }
    
    // Only auto-select default address if:
    // 1. Not in edit mode (no initialSelectedAddressId provided)
    // 2. User hasn't already manually selected an address
    if (!initialSelectedAddressId && !selectedAddressId.value) {
      if (defaultAddress) {
        selectedAddressId.value = defaultAddress.id;
      }
    } else {
      // User already has a selection or we're in edit mode, preserve it
      // If we have a selectedAddressId but it's not in the loaded addresses, 
      // validate it still exists
      if (selectedAddressId.value && !addresses.find(addr => addr.id === selectedAddressId.value)) {
        // The selected address no longer exists, clear selection
        selectedAddressId.value = undefined;
      }
    }
  };

  const selectAddress = (address: CustomerAddress) => {
    selectedAddressId.value = address.id;
    return address;
  };

  const clearAddresses = () => {
    customerAddresses.value = [];
    selectedAddressId.value = undefined;
  };

  const getSelectedAddress = () => {
    return customerAddresses.value.find(addr => addr.id === selectedAddressId.value) || null;
  };

  return {
    customerAddresses,
    selectedAddressId,
    isLoading,
    error,
    hasMultipleAddresses,
    loadCustomerAddresses,
    selectAddress,
    clearAddresses,
    getSelectedAddress,
    shouldShowAddresses,
  };
};
